// src/services/rbac.js
// Centralised role-based access control helpers.
// Roles (from DB constraint): admin | manager | viewer | supplier
//
// supplier – proveedor externo, solo ve su página "Mis servicios". Un músico
//            enlazado a su contacto de proveedor también la ve (sin cambiar de rol).
// viewer  – externo, solo ve eventos asignados, puede añadir gastos
// manager - empleado, ve/edita/añade/borra todo salvo gestión de usuarios
// admin   – control total
//
// Un rol desconocido, un perfil que no existe o está desactivado, o un error
// al leerlo dan NO_ACCESS_ROLE: sin permisos (antes caían en viewer).

import { getProfileByUserId, getUser } from "./teamService.js";

// ─── Permission matrix ───────────────────────────────────────────
const PERMISSIONS = Object.freeze({
    admin: {
        pages: ["dashboard", "events", "contacts", "venues", "finance", "emailing", "settings"],
        canView: true,
        canEdit: true,
        canAdd: true,
        canDelete: true,
        canManageUsers: true,
        // Comunicación interna al equipo (invites, mensajes). Desactivada mientras
        // el envío de correo desde el CRM esté apagado (EMAIL_SENDING_ENABLED).
        canSendInternal: false,
    },
    manager: {
        pages: ["dashboard", "events", "contacts", "venues", "finance", "emailing", "settings"],
        canView: true,
        canEdit: true,
        canAdd: true,
        canDelete: true,
        canManageUsers: false,
        canSendInternal: false,
    },
    viewer: {
        pages: ["dashboard", "events", "settings", "supplier"],
        canView: true,
        canEdit: false,
        canAdd: ["expenses"],          // solo puede añadir gastos
        canDelete: false,
        canManageUsers: false,
        canSendInternal: false,
    },
    supplier: {
        pages: ["supplier"],
        canView: false,
        canEdit: false,
        canAdd: false,
        canDelete: false,
        canManageUsers: false,
        canSendInternal: false,
    },
    none: {
        pages: [],
        canView: false,
        canEdit: false,
        canAdd: false,
        canDelete: false,
        canManageUsers: false,
        canSendInternal: false,
    },
});

export const NO_ACCESS_ROLE = "none";
const DEFAULT_ROLE = NO_ACCESS_ROLE;

// Página de inicio de cada rol. El proveedor (y quien no tiene acceso, que
// allí no verá ningún bolo) solo tiene "Mis servicios".
export function homeRouteForRole(role, isMobile) {
    if (role === "supplier" || !PERMISSIONS[role] || role === NO_ACCESS_ROLE) return "supplier";
    return isMobile ? "mobile" : "dashboard";
}

// ─── Cache ───────────────────────────────────────────────────────
let _cachedRole = null;

export function clearRoleCache() {
    _cachedRole = null;
}

// ─── Core helpers ────────────────────────────────────────────────

/**
 * Returns current user's role string (admin | manager | viewer | supplier | none).
 * Caches after first call until clearRoleCache(). Errors are not cached.
 */
export async function getUserRole() {
    if (_cachedRole) return _cachedRole;
    try {
        const user = await getUser();
        if (!user) return DEFAULT_ROLE;
        let profile;
        try {
            profile = await getProfileByUserId(user.id, { allowNull: false });
        } catch (e) {
            // PGRST116: no existe el perfil. Cualquier otro error (red, 5xx)
            // no se guarda: el siguiente intento vuelve a preguntar.
            if (e?.code !== "PGRST116") return DEFAULT_ROLE;
            profile = null;
        }
        const role = profile && profile.active !== false ? profile.role : null;
        _cachedRole = role && PERMISSIONS[role] ? role : DEFAULT_ROLE;
        return _cachedRole;
    } catch {
        return DEFAULT_ROLE;
    }
}

/**
 * Get the permissions object for the current (or given) role.
 */
export function getPermissions(role) {
    return PERMISSIONS[role] || PERMISSIONS[DEFAULT_ROLE];
}

// ─── Convenience checks ─────────────────────────────────────────

export async function isAdmin() {
    return (await getUserRole()) === "admin";
}

export async function isManager() {
    return (await getUserRole()) === "manager";
}

export async function isViewer() {
    return (await getUserRole()) === "viewer";
}

/**
 * ¿Puede el usuario actual enviar comunicación interna al equipo
 * (invites de calendario, mensajes a músicos/técnicos)? admin + manager.
 */
export async function canSendInternalEmail() {
    const role = await getUserRole();
    return !!(PERMISSIONS[role] || PERMISSIONS[DEFAULT_ROLE]).canSendInternal;
}

/**
 * can(action [, subAction])
 *
 * action: "view" | "edit" | "add" | "delete" | "manageUsers"
 * subAction: for "add" → resource name string (e.g. "expenses")
 *
 * Examples:
 *   await can("edit")           // manager/admin → true, viewer → false
 *   await can("add", "expenses") // viewer → true (only expenses)
 *   await can("delete")          // only admin
 */
export async function can(action, subAction) {
    const role = await getUserRole();
    const perms = PERMISSIONS[role] || PERMISSIONS[DEFAULT_ROLE];

    switch (action) {
        case "view":
            return perms.canView;
        case "edit":
            return perms.canEdit;
        case "add":
            if (perms.canAdd === true) return true;
            if (Array.isArray(perms.canAdd)) {
                return subAction ? perms.canAdd.includes(subAction) : false;
            }
            return false;
        case "delete":
            return perms.canDelete;
        case "manageUsers":
            return perms.canManageUsers;
        case "sendInternal":
            return !!perms.canSendInternal;
        default:
            return false;
    }
}

/**
 * Synchronous version — uses the cached role (returns false if not cached).
 * Useful in render functions where async is inconvenient.
 */
export function canSync(action, subAction) {
    const role = _cachedRole || DEFAULT_ROLE;
    const perms = PERMISSIONS[role] || PERMISSIONS[DEFAULT_ROLE];

    switch (action) {
        case "view":
            return perms.canView;
        case "edit":
            return perms.canEdit;
        case "add":
            if (perms.canAdd === true) return true;
            if (Array.isArray(perms.canAdd)) {
                return subAction ? perms.canAdd.includes(subAction) : false;
            }
            return false;
        case "delete":
            return perms.canDelete;
        case "manageUsers":
            return perms.canManageUsers;
        case "sendInternal":
            return !!perms.canSendInternal;
        default:
            return false;
    }
}

/**
 * Returns the cached role synchronously (or DEFAULT_ROLE if not loaded yet).
 */
export function getRoleSync() {
    return _cachedRole || DEFAULT_ROLE;
}
